import { axiosPrivate } from "@/api/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { toast } from "sonner";

export default function useCategoryMuations({
  resetForm,
  closeAdd,
  closeEdit,
  clearEditingId,
  closeConfirm,
}: {
  resetForm: () => void;
  closeAdd: () => void;
  closeEdit: () => void;
  clearEditingId: () => void;
  closeConfirm: () => void;
}) {
  const queryClient = useQueryClient();
  
  // Create feed category (POST feeds/categories)
  const createtCategoryMutation = useMutation({
    mutationFn: async (newCategory: { name: string; description?: string; icon_url?: string }) => {
      // Map to API schema; backend expects name, description, icon_url
      const payload = {
        name: newCategory.name,
        description: newCategory.description ?? "",
        icon_url: newCategory.icon_url ?? "",
      };
      const response = await axiosPrivate.post("/feeds/categories", payload);
      const c = response.data.data;
      return { id: Number(c.id ?? c.category_id), name: c.name };
    },
    onSuccess: (newCategory) => {
      queryClient.setQueryData(
        ["categories"],
        (oldData: { id: number; name: string }[] = []) => [...oldData, newCategory]
      );
      resetForm();
      closeAdd();
      toast.success("Feed category created successfully");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to create feed category");
    },
  });

  // Update feed category (PUT feeds/categories/{id})
  const editCategoryMutation = useMutation({
    mutationFn: async (updatedCategory: { id: number; name: string; description?: string; icon_url?: string }) => {
      const payload = {
        name: updatedCategory.name,
        description: updatedCategory.description ?? "",
        icon_url: updatedCategory.icon_url ?? "",
      };
      const response = await axiosPrivate.put(
        `/feeds/categories/${updatedCategory.id}`,
        payload
      );
      const c = response.data.data;
      return { id: Number(c.id ?? c.category_id), name: c.name };
    },
    onSuccess: (updatedCategory) => {
      queryClient.setQueryData(["categories"], (oldData: { id: number; name: string }[] = []) =>
        oldData.map((item) =>
          item.id === updatedCategory.id ? updatedCategory : item
        )
      );
      resetForm();
      closeEdit();
      clearEditingId();
      toast.success("Feed category updated successfully");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to update feed category");
    },
  });

  // Delete feed category (DELETE /feeds/categories/{id})
  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: number) => {
      await axiosPrivate.delete(`/feeds/categories/${id}`);
      return id;
    },
    onSuccess: (id) => {
      let removed = false;
      queryClient.setQueryData(["categories"], (oldData: { id: number; name: string }[] = []) => {
        const next = oldData.filter((item) => item.id !== id);
        removed = next.length !== oldData.length;
        return next;
      });
      if (!removed) {
        queryClient.invalidateQueries({ queryKey: ["categories"] });
      }
      clearEditingId();
      closeConfirm();
      toast.success("Feed category deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to delete feed category");
    },
  });

  return {
    createtCategoryMutation,
    editCategoryMutation,
    deleteCategoryMutation,
  };
}
